import { zodResolver } from '@hookform/resolvers/zod'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { FieldValues, FormProvider, Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import { KeylessIntegrationConfig } from './integration-card.types'

interface StatusPillProps {
  configured: boolean
}

const StatusPill = ({ configured }: StatusPillProps) => {
  const { t } = useTranslation(['organisation'])
  return (
    <span
      className={`keyless-card__status keyless-card__status--${configured ? 'connected' : 'pending'}`}
    >
      {configured
        ? t('organisation:places.integrations.keyless.status.connected')
        : t('organisation:places.integrations.keyless.status.notConfigured')}
    </span>
  )
}

interface IntegrationCardProps {
  buildingId: number
  // Note: intentionally the existential (`any`-erased) config type, not a generic
  // `<TValues>` param — RHF's `useForm`/`zodResolver` typings don't support a form whose
  // value shape is only known to be "some FieldValues", so the card is written once
  // against the erased shape and each provider's own files stay strongly typed.
  config: KeylessIntegrationConfig
  // True while a *different* card's submit is pending — see KeylessIntegrations, which
  // tracks a single page-wide "which integration is currently saving" id so an admin can't
  // start a second connect/activate while one is still in flight.
  isBlockedByOther: boolean
  isOpen: boolean
  onPendingChange: (isPending: boolean) => void
  onToggle: () => void
}

export const IntegrationCard = ({
  buildingId,
  config,
  isBlockedByOther,
  isOpen,
  onPendingChange,
  onToggle,
}: IntegrationCardProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const connection = config.useConnection(buildingId)
  const { submit, isPending } = config.useSubmit(buildingId)

  useEffect(() => {
    onPendingChange(isPending)
  }, [isPending, onPendingChange])

  const form = useForm({
    defaultValues: config.defaultValues,
    mode: 'onTouched',
    // Note: cast needed because `config.schema` is intentionally type-erased (ZodTypeAny,
    // see integration-card.types.ts) — zodResolver's generics can't express "a schema for
    // some FieldValues shape not known here".
    resolver: zodResolver(
      config.schema(t) as Parameters<typeof zodResolver>[0],
    ) as Resolver<FieldValues>,
    // Note: mirrors legacy Formik's `enableReinitialize` — the form re-syncs whenever the
    // fetched connection data changes.
    values: connection.values,
  })

  const isLocked = config.lockAfterConnect && connection.isConfigured
  const isFieldsDisabled = isPending || isBlockedByOther
  const isSaveDisabled = isLocked || isFieldsDisabled
  const buttonLabel = isLocked
    ? t('organisation:places.integrations.keyless.connectedButton', { label: config.label(t) })
    : connection.isConfigured
      ? t('organisation:places.integrations.keyless.update')
      : t('common:actions.save')

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await submit(values)
      toastFactory.success(t('organisation:places.integrations.keyless.toasts.saved'))
    } catch (error) {
      toastFactory.error(
        getApiErrorMessage(error, {
          message: t('organisation:places.integrations.keyless.toasts.saveFailed'),
        }),
      )
    }
  })

  return (
    <div className={`keyless-card ${isOpen ? 'keyless-card--expanded' : ''}`}>
      <button className="keyless-card__header" type="button" onClick={onToggle}>
        <span className="keyless-card__title">{config.label(t)}</span>
        <span className="keyless-card__header-right">
          <StatusPill configured={connection.isConfigured} />
          {isOpen ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
        </span>
      </button>
      {isOpen && (
        <div className="keyless-card__body">
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              <fieldset className="keyless-card__fieldset" disabled={isFieldsDisabled}>
                <config.Fields
                  buildingId={buildingId}
                  disabled={isFieldsDisabled}
                  isConfigured={connection.isConfigured}
                />
              </fieldset>
              <button
                className={`button ${isLocked ? 'button-gray' : 'button-primary'}`}
                disabled={isSaveDisabled}
                title={
                  isBlockedByOther
                    ? t('organisation:places.integrations.keyless.blockedTooltip')
                    : undefined
                }
                type="submit"
              >
                {buttonLabel}
              </button>
            </form>
          </FormProvider>
        </div>
      )}
    </div>
  )
}
